import {useState, useEffect, useMemo, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {crudAPI} from '../../../apis'
import {useIsMounted} from '../../../hooks'

import * as Yup from 'yup'
import {parsePhoneNumberFromString} from 'libphonenumber-js'

import {showAlert} from '../../../actions'
import {useDispatch} from 'react-redux'

import {
  Button,
  Dialog,
  TextField,
  IconButton,
  Autocomplete,
  createFilterOptions
} from '@mui/material'

import {DataGrid} from '@mui/x-data-grid'

import CloseIcon from '@mui/icons-material/Close'

import RSVPMetrics from './RSVPMetrics'
import RSVPCustomToolbar from './RSVPCustomToolbar'

import ContactsManager from '../../generics/forms/formik/ContactsManager'



const styles = {
  root: css`
    height: 100%;
    position: relative;
    padding: 40px;
    
    h3 { 
      margin-top: 25px; 
      font-weight: 500; 
      font-size: 1.2rem 
    }

    @media (max-width: 800px) {
      padding: 20px;
    }
  `,
  header: css`
    display: flex; 
    margin-bottom: 30px;

    p { 
      color: grey;
      font-weight: 500;
      font-size: 0.9rem;
    }

    span {
      font-weight: 600;
    }
  `,
  groupSelectionContainer: css`
    margin-bottom: 20px;

    h4 {
      font-size: 1.1rem;
      margin-bottom: 0;
      font-weight: 500;
    }

    p {
      color: grey;
      font-weight: 500;
      font-size: 0.9rem;
      margin-bottom: 20px;
    }
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: 15px; 
    margin-bottom: 30px;
    
    & > div { 
      display: flex; 
      gap: 20px; 
      flex-wrap: wrap 
    }
  `,
  field: css`
    min-width: 240px;
    max-width: 300px;
    flex: 1 1 260px;
  `,
  button: css`
    min-width: 100px;
    max-width: 140px;
    height: 42px; 
    box-shadow: none;
    font-weight: 500; 
    text-transform: uppercase; 
    font-size: 0.8rem;
    margin-top: 10px;
    
    &:hover { 
      box-shadow: none; 
    }
  `,
  table: css`
    margin-top: 30px;
  `,
  close: css`
    position: absolute;
    top: 10px; 
    right: 10px;
  `,
  groupDropdown: css`
    margin-bottom: 40px;
  `,
}


const STATUS_COLORS = {
  'not sent': '#6c757d',
  maybe:      '#17a2b8',
  pending:    '#FFA500',
  declined:   '#dc3545',
  confirmed:  '#28a745',
}


const inviteSchema = Yup.object({
  name:  Yup.string().trim().required('Name is required'),
  email: Yup.string().trim().email('Invalid email').notRequired(),
  phone: Yup.string().trim().notRequired()
    .test('is-phone','Invalid phone', (v) => {
      if (!v) return true
      const p = parsePhoneNumberFromString(v,'US')
      return p ? p.isValid() : false
    })
})
.test('email-or-phone','Provide email or phone', ({email,phone}) => !!email || !!phone)



const CREATE_OPTION = { id: '__create__', name: 'Create List…', isCreate: true }
// ———————————————————————————————————————————————
// Main component
// ———————————————————————————————————————————————

const RSVPInviteForm = ({ eventID, eventName, close }) => {
  const isXl = useMediaQuery(theme => theme.breakpoints.up('lg'))

  const dispatch  = useDispatch()

  const isMounted = useIsMounted()

  // Already‐sent RSVPs
  const [rsvps, setRsvps] = useState([])
  const [totalInvited, setTotalInvited] = useState(0)
  const [totalPending, setTotalPending] = useState(0)
  const [totalDeclined, setTotalDeclined] = useState(0)
  const [totalConfirmed, setTotalConfirmed] = useState(0)

  const [search, setSearch] = useState('')

  const [page, setPage] = useState(0)
  const [links, setLinks] = useState({})
  const [pageCache, setPageCache] = useState({}) // pageNumber: [rows]

  // Manual invite form
  const [manual, setManual] = useState({name:'', email:'', phone:''})

  // Normalized groups: [{id,name,contacts:[{id,name,email,phone}]}]
  const [groups, setGroups] = useState([])
  const dropdownOptions = useMemo(() => {
    // your real groups plus the “create” option at the end
    return [...groups, CREATE_OPTION]
  }, [groups])


  const [groupInput, setGroupInput] = useState('')
  const [selectedGroup, setSelectedGroup] = useState(null)


  const [rowSelectionModel, setRowSelectionModel] = useState({
    ids: new Set(), type: 'include'
  })

  const [openContactsManager, setOpenContactsManager] = useState(false)

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)



  const allRows = useMemo(() => {
    // If no group selected, just show all RSVPs (deduped by email/phone)
    if (!selectedGroup) {
      return rsvps.map(r => ({
        ...r,
        id: (r.email || r.phone)
      }))
    }

    // 1) Build a map from the RSVP list keyed on email||phone
    const rsvpMap = rsvps.reduce((map, r) => {
      const key = r.email || r.phone
      if (key) map[key] = r
      return map
    }, {})

    // 2) For each contact in the group, either use their RSVP or show them 'Not Sent'
    const groupRows = selectedGroup.contacts.map(c => {
      const key = c.email || c.phone
      const r = rsvpMap[key]
      if (r) {
        // use the existing RSVP record, but remap its id to our key
        return { ...r, id: key }
      } else {
        // no RSVP yet, so use the contact info
        return {
          id:     key,
          name:   c.name,
          email:  c.email,
          phone:  c.phone,
          status: 'not sent'
        }
      }
    })

    // 3) Any RSVPs outside this group? append them, too.
    const extraRows = rsvps
      .filter(r => {
        const key = r.email || r.phone
        return !selectedGroup.contacts.some(c => (c.email || c.phone) === key)
      })
      .map(r => ({ ...r, id: (r.email || r.phone) }))

    return [...groupRows, ...extraRows]
  }, [rsvps, selectedGroup])


  // Counts for metrics bar
  const counts = useMemo(() => {
    // compute “maybe” locally
    const maybeCount = totalInvited - totalPending - totalConfirmed - totalDeclined
    return {
      invited:   totalInvited,
      maybe:     maybeCount,
      pending:   totalPending,
      confirmed: totalConfirmed,
      declined:  totalDeclined,
    }
  }, [totalInvited, totalPending, totalConfirmed, totalDeclined])


  const filter = createFilterOptions({stringify: opt => opt.name})
  const sendDisabled = !allRows.some(r => r.status === 'not sent')



  // Load existing RSVPs
  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        const [rsvpRes, groupRes] = await Promise.all([
          crudAPI.index(`/performances/${eventID}/rsvps`),
          crudAPI.index('/contacts/contact_groups?include=contacts')
        ])
        if (!isMounted.current) return

        const invited   = rsvpRes?.data.links?.count
        const pending   = rsvpRes?.data?.meta?.total_pending
        const declined  = rsvpRes?.data?.meta?.total_declined
        const confirmed = rsvpRes?.data?.meta?.total_confirmed
        
        const parsedInvited   = parseInt(invited, 10)   || 0
        const parsedConfirmed = parseInt(confirmed, 10) || 0
        const parsedDeclined  = parseInt(declined, 10)  || 0
        const parsedPending   = parseInt(pending, 10)   || 0

        setTotalInvited(parsedInvited)
        setTotalConfirmed(parsedConfirmed)
        setTotalPending(parsedPending)
        setTotalDeclined(parsedDeclined)

        // — RSVPs as before —
        setRsvps(rsvpRes.data.data.map(r => ({
          id:     r.id,
          name:   r.attributes.name,
          email:  r.attributes.email,
          phone:  r.attributes.phone,
          status: r.attributes.status
        })))

        // - Links -
        setLinks(rsvpRes?.data.links)

        // — normalize groups + contacts —
        const rawGroups = groupRes.data.data // [ { id, attributes, relationships: { contacts: { data: [ {id} ] } } } ]
        const inc       = groupRes.data.included || []  // [ { type: 'contact', id, attributes: { name, email, phone } } ]

        // 1) build a map from contact ID → contact attrs
        const contactMap = inc.filter(i => i.type === 'contact').reduce((map, c) => {
          map[c.id] = { id: c.id, ...c.attributes }
          return map
        }, {})

        // 2) for each group, pull its contact IDs from the group’s relationship
        const norm = rawGroups.map(g => {
          const contactIds = g.relationships.contacts.data.map(rel => rel.id)
          const contacts   = contactIds.map(id => contactMap[id]).filter(Boolean)
          return {
            id: g.id,
            name: g.attributes.name,
            contacts
          }
        })

        setGroups(norm)
      } catch (err) {
        console.error(err)
      } finally {
        if (isMounted.current) setLoading(false)
      }
    })()
  }, [eventID, isMounted])



const fetchRsvpsPage = useCallback(async (requestedPage) => {
  // Prevent out-of-bounds page fetch
  if (links?.pages && requestedPage + 1 > links.pages) {
    return
  }

  // Check cache first
  if (pageCache[requestedPage]) {
    setRsvps(pageCache[requestedPage])
    return
  }

  setLoading(true)
  try {
    // DataGrid uses 0-based page; backend uses 1-based
    const url = `/performances/${eventID}/rsvps?page=${requestedPage + 1}`
    
    const response  = await crudAPI.index(url)
    const newLinks  = response.data.links
    const pageData  = response.data
    const newRsvps = pageData.data.map(r => ({
      id:     r.id,
      name:   r.attributes.name,
      email:  r.attributes.email,
      phone:  r.attributes.phone,
      status: r.attributes.status,
    }))

    if (isMounted.current) {
      setRsvps(newRsvps)
      setLinks(newLinks)
      setPageCache(prev => ({ ...prev, [requestedPage]: newRsvps }))
    }
  } catch (err) {
    console.error(err)
    // optionally dispatch an error alert here
  } finally {
    if (isMounted.current) setLoading(false)
  }
}, [eventID, links?.pages, isMounted, pageCache])




  // Handle inline edits
  const handleCellEditCommit = useCallback(({ id, field, value }) => {
    setRsvps(prev => prev.map(r => r.id === id ? {...r, [field]: value} : r))
  }, [])

  // Helper to append a brand new group
  const handleNewGroup = (group) => {setGroups(g => [...g, group])}

  // Send RSVPs for "not sent" rows
  const handleSendRsvps = useCallback(async () => {
    setLoading(true)

    // 1) send _all_ rows with status 'not sent'
    const toSend = allRows.filter(r => r.status === 'not sent')

    try {
      const sent = await Promise.all(toSend.map(async r => {
        const resp = await crudAPI.create(
          `/performances/${eventID}/rsvps/invite`,
          { 
            name:  r.name, 
            email: r.email || undefined, 
            phone: r.phone || undefined 
          }
        )
        const d = resp.data.data
        return {
          id:     d.id,
          name:   d.attributes.name,
          email:  d.attributes.email,
          phone:  d.attributes.phone,
          status: d.attributes.status
        }
      }))

      // 2) merge in the newly‐sent ones
      setRsvps(curr => [...curr, ...sent])

      // if you still want to clear any selection:
      setRowSelectionModel({ ids: new Set(), type: 'include' })
      dispatch(showAlert('RSVPs sent', 'success'))
    } catch (err) {
      dispatch(showAlert('Something went wrong. Try again.', 'error'))
    } finally {
      setLoading(false)
    }
  }, [allRows, eventID, dispatch])



  // Manual invite
  const handleManualInvite = async (e) => {
    e.preventDefault()
    setErrors({})
    try {
      await inviteSchema.validate(manual,{abortEarly: false})
      setErrors({})
      const resp = await crudAPI.create(
        `/performances/${eventID}/rsvps/invite`,
        {name: manual.name, email: manual.email || undefined, phone: manual.phone || undefined}
      )
      const d = resp.data.data
      setRsvps(r => [...r, {
        id: d.id,
        name: d.attributes.name,
        email: d.attributes.email,
        phone: d.attributes.phone,
        status: d.attributes.status
      }])
      setManual({name:'', email:'', phone:''})
    } catch (err) {
      const messages = err.response?.data?.error?.messages
      if (messages) {
        // clear any old errors
        setErrors({})

        // priority: email → phone → user_id → generic
        if (messages.email) {
          const msg = `RSVP already sent to ${manual.email}`
          dispatch(showAlert(msg, 'warning'))
          setErrors({ email: msg })
        }
        else if (messages.phone) {
          const msg = `RSVP already sent to ${manual.phone}`
          dispatch(showAlert(msg, 'warning'))
          setErrors({ phone: msg })
        }
        else if (messages.user_id) {
          const msg = `RSVP already sent to ${manual.email}`
          dispatch(showAlert(msg, 'warning'))
          // use whichever field makes sense — probably email
          setErrors({ email: msg })
        }
        else {
          // some other server‐side validation error
          const combined = Object.values(messages).flat().join(', ')
          const formMsg  = `Invitation failed: ${combined}`
          setErrors({ form: formMsg })
        }
      }
      else if (err.name === 'ValidationError') {
        // yup client‐side
        const m = {}
        err.inner.forEach(e => {m[e.path] = e.message})
        setErrors(m)
      }
      else {
        dispatch(showAlert('Failed to send RSVP, please try again', 'error'))
      }
    }
  }


  const columns = [
    { field:'name',  headerName: 'Name',  flex: 1, minWidth: 150 },
    { field:'email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field:'phone', headerName: 'Phone', flex: 1, minWidth: 150 },
    {
      field:'status',
      headerName:'Status',
      minWidth: 120,
      renderCell: (params) => {
        const c = STATUS_COLORS[params.value] || '#000'
        return <span
          style={{
            padding: '4px 12px',
            borderRadius: 3,
            backgroundColor: `${c}1A`,
            color: c,
            textTransform: 'uppercase',
            fontWeight: 500,
            fontSize: '0.7rem'
          }}
        >
          {params.value}
        </span>
      }
    }
  ]


  function closeContactsManager() {
    setOpenContactsManager(false)
  }


  const handleExportAll = async () => {
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : ''
      const response = await crudAPI.show(`/performances/${eventID}/rsvps/export_all.csv${query}`)

      // Get the filename from Content-Disposition, fallback to "rsvps.csv"
      let filename = 'rsvps.csv'
      const disposition = response.headers['content-disposition']
      if (disposition && disposition.indexOf('filename=') !== -1) {
        filename = disposition.split('filename=')[1].replace(/[";]/g, '').trim()
      }

      // Create a download link
      const url  = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href  = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      alert("Download failed")
      console.error(err)
    }
  }


  return (
    <div css={styles.root}>
      <IconButton css={styles.close} onClick={close}>
        <CloseIcon/>
      </IconButton>

      <div css={styles.header}>
        <div>
          <h3>Send RSVPs for {eventName}</h3>
          <p>Add a name and email or phone, then tap <span>Send RSVP</span>.</p>
        </div>
      </div>


      <form css={styles.form}>        
        <div>
          <TextField
            css={styles.field}
            label='Name'
            variant='filled'
            fullWidth
            value={manual.name}
            onChange={e => setManual(m => ({...m, name: e.target.value}))}
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            css={styles.field}
            label='Email'
            variant='filled'
            fullWidth
            value={manual.email}
            onChange={e => setManual(m => ({...m, email: e.target.value}))}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            css={styles.field}
            label='Phone'
            variant='filled'
            fullWidth
            value={manual.phone}
            onChange={e => setManual(m => ({...m, phone: e.target.value}))}
            error={!!errors.phone}
            helperText={errors.phone}
          />
        </div>

        <Button css={styles.button} onClick={handleManualInvite}>
          Send RSVP
        </Button>
      </form>


      <div css={styles.groupSelectionContainer}>
        <h4>Select or Create a Group</h4>
        <p>
          Select a pre-saved contact list to send RSVPs to multiple people at 
          once or create a contact list.
        </p>

        <div css={styles.groupDropdown}>
          <Autocomplete
            freeSolo
            options={dropdownOptions}
            getOptionLabel={option => option.name} 
            value={selectedGroup}
            inputValue={groupInput}
            onInputChange={(e, val, reason) => {
              if (reason === 'input') {
                setGroupInput(val)
              }
            }}
            onChange={(_, option) => {
              if (option?.isCreate) {
                setOpenContactsManager(true)
              } else if (option) {
                setSelectedGroup(option)
                setGroupInput(option.name)
              }
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) {
                return options
              }
              const filtered = filter(options, { inputValue })
              // Always append your "Create List…" sentinel if it isn't already there
              if (!filtered.find(o => o.id === CREATE_OPTION.id)) {
                filtered.push(CREATE_OPTION)
              }
              return filtered
            }}
            sx={{
              maxWidth: 300,
              cursor: 'pointer',
              '& .MuiFilledInput-root': {
                cursor: 'pointer',
                paddingTop: 0,
                paddingBottom: 0,
              },
              '& .MuiFilledInput-input': {
                cursor: 'pointer',
                padding: '8px 12px !important',
              }
            }}
            renderOption={(optionProps, option) => {
              const { key, ...liProps } = optionProps
              // If this is “Create List” option…
              const style = option.isCreate
                ? { color: '#1976d2', fontWeight: 500 }
                : {}

              return (
                <li {...liProps} key={key} style={style}>
                  {option.name}
                </li>
              )
            }}
            renderInput={params => 
              <TextField 
                {...params} 
                variant='filled' 
                placeholder='Select saved contacts' 
              />
            }
          />
        </div>
      </div>

      <RSVPMetrics total={totalInvited} counts={counts} />

      <div css={styles.table}>
        <h3>Invited</h3>
        <DataGrid
          rows={allRows}
          columns={columns}
          showToolbar
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          onCellEditCommit={handleCellEditCommit}
          slots={{toolbar: RSVPCustomToolbar}}
          slotProps={{
            toolbar: {
              search,
              setSearch,
              onSend: handleSendRsvps,
              onExport: handleExportAll,
              sendDisabled: sendDisabled
            }
          }}
          page={page}
          rowCount={totalInvited}
          pageSize={40}
          paginationModel={{ page, pageSize: 40 }}
          loading={loading}
          pagination
          paginationMode='server'
          onPaginationModelChange={({ page: newPage }) => {
            fetchRsvpsPage(newPage)
            setPage(newPage)
          }}
          sx={{
            border: 0,
            marginTop: '25px',
            borderRadius: '8px',
            paddingBottom: '60px',

            // Cell
            '& .MuiDataGrid-cell': {
              fontSize: '0.93rem',
              borderBottom: 'none',
              borderTop: 'none',
              cursor: 'pointer'
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            // Row
            '& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {
              outline: 'none',
            },
            // Header
            '& .MuiDataGrid-columnHeader': {
              background: 'rgba(255,241,209,0.4)',
              textTransform: 'uppercase',
              border: 'none',
              fontSize: '0.8rem',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            }
          }}
        />
      </div>


      <Dialog 
        open={openContactsManager} 
        onClose={closeContactsManager} 
        fullScreen={!isXl}
        fullWidth
        PaperProps={{
          sx: {
            height: isXl ? '90vh' : null,
            maxHeight: 'none',
            maxWidth: '1200px',
            minWidth: 0
          }
        }}>
        <ContactsManager close={closeContactsManager} onGroupCreated={handleNewGroup} />
      </Dialog>
    </div>
  )
}


export default RSVPInviteForm



